<template>
  <main class="px-4 pt-4">
    <h1 class="mb-4">
      {{ $t('Companies') }}
    </h1>

    <dx-data-grid
      ref="companiesGrid"
      :data-source="dataSource"
      :remote-operations="remoteOperations"
      :allow-column-reordering="true"
      :show-borders="true"
      :column-min-width="50"
      :column-auto-width="true"
      :allow-column-resizing="true"
      column-resizing-mode="widget"
      :hover-state-enabled="true"
      :on-row-dbl-click="onRowDblClick"
      @toolbar-preparing="onToolbarPreparing"
    >
      <dx-column
        data-field="id"
        caption="ID"
        fixed-position="left"
        :visible="false"
        :allow-editing="false"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="inn"
        :caption="$t('INN')"
      />
      <dx-column
        data-field="name"
        :caption="$t('Organization name')"
      />
      <dx-column
        data-field="freight_forwarding_contract"
        :caption="$t('freight forwarding contract')"
      />
      <dx-column
        data-field="freight_forwarding_contract_date"
        :caption="$t('freight forwarding contract date')"
        data-type="date"
      />
      <dx-column
        data-field="contract_nums"
        :caption="$t('Contract nums')"
      />
      <dx-column
        data-field="notification_msg"
        :caption="$t('notification to client')"
      />
      <dx-column
        data-field="debtor"
        :caption="$t('debtor')"
      />

      <template #newCompanyBtn="{}">
        <dx-button
          :text="$t('New company')"
          type="success"
          icon="ace-icon icon-plus"
          styling-mode="contained"
          class="custom-btn mr-2"
          @click="addNewCompany"
        />
      </template>
      <dx-group-panel :visible="true" />
      <dx-editing
        :allow-updating="true"
        :allow-deleting="false"
        :allow-adding="isMobile"
        :use-icons="true"
        mode="row"
      />
      <dx-header-filter :visible="true" />
      <dx-search-panel
        :visible="true"
        :highlight-case-sensitive="false"
      />
      <dx-export
        :enabled="true"
        :allow-export-selected-data="true"
        file-name="freight-orders"
      />
      <dx-column-chooser :enabled="true" />
      <dx-selection
        show-check-boxes-mode="always"
        mode="multiple"
      />
      <dx-state-storing
        :enabled="true"
        type="localStorage"
        :storage-key="`u${user.id}-companies-grid-storage`"
      />
      <dx-paging :page-size="20" />
      <dx-pager
        :show-page-size-selector="true"
        :allowed-page-sizes="[10, 15, 20, 50]"
      />
    </dx-data-grid>
  </main>
</template>

<script>
import {
  DxDataGrid,
  DxExport,
  DxColumn,
  DxColumnChooser,
  DxEditing,
  DxHeaderFilter,
  DxGroupPanel,
  DxSearchPanel,
  DxSelection,
  DxStateStoring,
  DxPaging,
  DxPager,
} from 'devextreme-vue/data-grid';

import { DxButton } from 'devextreme-vue';

import { mapState } from 'vuex';
import ruMessages from 'devextreme/localization/messages/ru.json';
import { locale as localize, loadMessages } from 'devextreme/localization';

import mixinDetictingMobile from '@/mixins/mixin-detecting-mobile';

import createCustomStore from '@/utils/create-custom-store';

export default {
  name: 'CompaniesGrid',
  components: {
    DxDataGrid,
    DxColumn,
    DxGroupPanel,
    DxSearchPanel,
    DxEditing,
    DxHeaderFilter,
    DxExport,
    DxColumnChooser,
    DxSelection,
    DxStateStoring,
    DxPaging,
    DxPager,
    DxButton,
  },
  mixins: [mixinDetictingMobile],
  data: () => ({
    remoteOperations: {
      sorting: true,
      filtering: true,
      paging: true,
    },
    dataSource: createCustomStore('profile/api/companies/'),
    loading: false,
  }),
  computed: {
    ...mapState(['user']),
  },
  created() {
    loadMessages(ruMessages);
    localize(this.$i18n.locale);
  },
  methods: {
    onToolbarPreparing(e) {
      e.toolbarOptions.items.unshift({
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'ace-icon icon-refresh',
          onClick: this.refreshDataGrid.bind(this),
        },
      }, {
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'ace-icon icon-close',
          hint: this.$t('clear_filters'),
          onClick: this.clearFilter.bind(this),
        },
      });

      if (!this.isMobile) {
        e.toolbarOptions.items.unshift({
          location: 'after',
          widget: 'dxButton',
          template: 'newCompanyBtn',
        });
      }
    },
    onRowDblClick(e) {
      if (e.data.is_expedition_company && !this.user.is_head && !this.user.is_administrator) {
        this.$bvToast.toast(this.$t('access_denied'), {
          title: this.$t('error'),
          variant: 'danger',
          solid: true,
          autoHideDelay: 15000,
        });
        return;
      }
      this.$router.push(`/company/edit/${e.data.id}`);
    },
    addNewCompany() {
      this.$router.push('/company/add');
    },
    refreshDataGrid() {
      this.$refs.companiesGrid.instance.refresh();
    },
    clearFilter() {
      this.$refs.companiesGrid.instance.clearFilter();
    },
    async onSubmit() {
      this.loading = true;
      try {
        if (this.isEditForm) {
          await this.$http.patch(`profile/api/companies/${this.selectedCompany.id}/`, this.selectedCompany);
        } else {
          await this.$http.post('profile/api/companies/', this.selectedCompany);
        }
        this.refreshDataGrid();
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
